import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useListBlogReleases, useResolveBlogRelease, getListBlogReleasesQueryKey, getListAdminBlogPostsQueryKey, getListBlogPostsQueryKey } from '@workspace/api-client-react';

export function BlogReleases({ csrfToken, disabled, onChanged }: { csrfToken: string; disabled: boolean; onChanged: () => void }) {
  const query = useListBlogReleases({ query: { queryKey: getListBlogReleasesQueryKey(), refetchInterval: 30000 } });
  const resolve = useResolveBlogRelease({ request: { headers: { 'X-CSRF-Token': csrfToken } } });
  const cache = useQueryClient();
  const [message, setMessage] = useState('');
  const pending = query.data?.reports.filter(r => ['conflict', 'blocked', 'stale'].includes(r.outcome)) ?? [];
  if (query.isError) return <p role="alert" className="mb-6">Release status could not be loaded. <button onClick={() => query.refetch()}>Retry</button></p>;
  if (!pending.length) return null;
  return <section aria-label="Content release review" className="mb-10 border border-border p-5 space-y-5" data-testid="blog-release-review">
    <h2 className="font-medium">Content release needs review</h2>
    <p className="text-sm">Live content has not been overwritten. Review the saved entry and proposed source before resolving. Save or discard unsaved edits first.</p>
    {pending.map(r => {
      const entry = query.data?.entries.find(e => e.id === r.id);
      return <div key={r.id} className="space-y-3 border-t border-border pt-4">
        <p><strong>{r.id}</strong> · source version {r.version}</p>
        <p className="text-sm">{r.reason}</p>
        {entry?.fields && <details><summary>Review proposed source (raw Markdown)</summary>
          <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-96 mt-3">{JSON.stringify(entry.fields, null, 2)}</pre>
        </details>}
        <p className="text-xs break-all">Current content hash: {r.currentHash ?? 'No saved entry'}</p>
        {r.outcome !== 'stale' && <div className="flex gap-4 flex-wrap">{(['keep', 'apply'] as const).map(choice => <button
          key={choice} className="border px-3 py-2 text-sm disabled:opacity-40"
          disabled={disabled || resolve.isPending}
          onClick={() => {
            if (!window.confirm(choice === 'keep' ? 'Keep the current database content and acknowledge this source version? Future starts will not apply it.' : 'Replace the saved entry with the reviewed release source? This can publish or withdraw it. The replaced version is kept in history.')) return;
            setMessage('');
            resolve.mutate({ id: r.id, data: { sourceHash: r.sourceHash, currentHash: r.currentHash, choice } }, {
              onSuccess: () => {
                cache.invalidateQueries({ queryKey: getListBlogReleasesQueryKey() });
                cache.invalidateQueries({ queryKey: getListAdminBlogPostsQueryKey() });
                cache.invalidateQueries({ queryKey: getListBlogPostsQueryKey() });
                onChanged();
                setMessage('Resolution saved. Other entries in the release were checked again.');
              },
              onError: () => { setMessage('Resolution failed or content changed. Refresh and review again.'); query.refetch(); },
            });
          }}>{choice === 'keep' ? 'Keep saved content' : 'Apply reviewed source'}</button>)}</div>}
      </div>;
    })}
    {message && <p role="status">{message}</p>}
  </section>;
}
